import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CommonDetail from '../common/common-detail';
import CommonTableList from '../common/common-table-list';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnsType } from 'antd/es/table';
import { api_good_receive } from '../../api/product';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { goodReceive, initGoodReceive } from '../../model/good-receive';
import GoodReceiveDetail from './good-receive-detail';
import GoodReceivePrint, { printGoodReceiveDocument } from './good-receive-print';
import { Button, Modal, Tag } from 'antd';
import { isEmpty } from '../../utils/util';

const GoodReceive: React.FC = () => {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('');
    const [title, setTitle] = useState('');
    const [data, setData] = useState<any[]>([]);
    const [goodReceive, setGoodReceive] = useState<goodReceive>(initGoodReceive);

    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [printData, setPrintData] = useState<goodReceive>(initGoodReceive);
    const printRef = useRef<HTMLDivElement>(null);

    const filterField: any = ['document_no', 'vendor_name', 'purchase_order_doc_no'];

    const placeholder_txt: string = 'เลขที่เอกสาร / ผู้ขายส่งสินค้า / เลขที่ PO';

    const columns: ColumnsType<any> = [
        { 
            title: 'สถานะ', 
            dataIndex: 'status', 
            key: 'status', 
            width: '130px', 
            fixed: 'left',
            render: (status: number) => status === 2 ? (
                <Tag color="green" style={{ fontWeight: 600 }}>🟢 รับเข้าแล้ว</Tag>
            ) : (
                <Tag color="orange" style={{ fontWeight: 600 }}>🟡 ฉบับร่าง</Tag>
            )
        },
        { 
            title: 'เลขที่เอกสาร', 
            dataIndex: 'document_no', 
            key: 'document_no', 
            width: '160px', 
            fixed: 'left',
            render: (docNo: string, record: any) => (
                <span 
                    style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)', cursor: 'pointer' }}
                    onClick={() => onShowDetail(record.status === 2 ? 'VIEW' : 'EDIT', record)}
                >
                    {docNo || '-'}
                </span>
            )
        },
        { 
            title: 'เลขที่ใบสั่งซื้อ (PO)', 
            dataIndex: 'purchase_order_doc_no', 
            key: 'purchase_order_doc_no', 
            width: '160px',
            render: (poDoc: string) => poDoc ? (
                <Tag color="blue" style={{ fontWeight: 600 }}>{poDoc}</Tag>
            ) : '-'
        },
        { title: 'วันที่รับเข้า', dataIndex: 'create_date', key: 'create_date', width: '180px' },
        { title: 'ผู้ขายส่งสินค้า', dataIndex: 'vendor_name', key: 'vendor_name' },
        { title: 'จำนวนรวม', dataIndex: 'quantity', key: 'quantity', width: '100px', align: 'right' },
        { title: 'ต้นทุนรวม', dataIndex: 'cost', key: 'cost', width: '120px', align: 'right', render: (val) => Number(val || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }) },
        { 
            title: 'การดำเนินการ', 
            dataIndex: 'operation', 
            width: '180px', 
            fixed: 'right', 
            render: (_, record) => (
                <div className='iconOperation'>
                    {record.status === 2 ? (
                        <>
                            <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => { onShowDetail('VIEW', record); }} />
                            <FontAwesomeIcon icon="print" title="พิมพ์ใบรับเข้าสินค้า" onClick={() => { onOpenPrintVoucher(record); }} />
                            <FontAwesomeIcon icon="trash-can" title="ลบ" onClick={() => { onDelete(record.id); }} />
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => { onShowDetail('VIEW', record); }} />
                            <FontAwesomeIcon icon="pen-to-square" title="แก้ไข" onClick={() => { onShowDetail('EDIT', record); }} />
                            <FontAwesomeIcon icon="print" title="พิมพ์ใบรับเข้าสินค้า" onClick={() => { onOpenPrintVoucher(record); }} />
                            <FontAwesomeIcon icon="trash-can" title="ลบ" onClick={() => { onDelete(record.id); }} />
                        </>
                    )}
                </div>
            )
        }
    ];

    const init = async () => {
        setData([]);
        try {
            let getGoodReceive = await api_good_receive('LIST', {});
            setData(Array.isArray(getGoodReceive) ? getGoodReceive : []);
        } catch (err) {
            console.error('Fetch Good Receive list error:', err);
            setData([]);
        }
    };

    const getConfigModal = (title: string, content: string) => {
        return {
            title: title,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
        };
    };

    const onShowDetail = async (mode: string, goodReceiveData?: goodReceive) => {
        const isConfirmed = goodReceiveData?.status === 2;
        const actualMode = isConfirmed ? 'VIEW' : mode;

        if (actualMode === 'ADD' || actualMode === 'COPY') {
            setTitle('เพิ่มการรับเข้า (ฉบับร่าง)');
            setGoodReceive(initGoodReceive);
        } else {
            setTitle(isConfirmed ? 'รายละเอียดการรับเข้า (รับเข้าแล้ว)' : (actualMode === 'EDIT' ? 'แก้ไขการรับเข้า' : 'รายละเอียดการรับเข้า'));
            if (goodReceiveData && goodReceiveData.id) {
                try {
                    const fullData = await api_good_receive('GET', {}, goodReceiveData.id);
                    if (fullData && fullData.id) {
                        setGoodReceive(fullData);
                    } else {
                        setGoodReceive(goodReceiveData);
                    }
                } catch (err) {
                    console.error('Fetch GR details error:', err);
                    setGoodReceive(goodReceiveData);
                }
            } else {
                setGoodReceive(goodReceiveData !== undefined && goodReceiveData !== null ? goodReceiveData : initGoodReceive);
            }
        }

        setMode(actualMode);
        setOpen(true);
    };

    const onCloseDetail = () => {
        setOpen(false);
    };

    const performSave = async (targetStatus: number = 1) => {
        let operation = 'INSERT';
        let key_id = undefined;
        let body = JSON.parse(JSON.stringify(goodReceive));
        body.status = targetStatus;

        let validate = true;
        let validate_text = "ข้อมูลไม่ถูกต้อง";

        if (goodReceive.id > 0) {
            operation = targetStatus === 2 ? 'CONFIRM' : 'UPDATE';
            key_id = goodReceive.id;
        }

        if (targetStatus === 2 && (!body['detail'] || body['detail'].length === 0)) {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'กรุณาเพิ่มรายการสินค้าก่อนยืนยันรับเข้าสินค้า')
            });
            return false;
        }

        // ZERO QUANTITY RULE: rows left at 0 are not received and create no stock
        // movement, so confirming requires at least one line with a positive quantity.
        if (targetStatus === 2) {
            const receivedLines = (body['detail'] || []).filter((obj: any) => (Number(obj['quantity']) || 0) > 0);
            if (receivedLines.length === 0) {
                Modal.warning({
                    ...getConfigModal('แจ้งเตือน', 'กรุณาระบุจำนวนสินค้าที่รับเข้าอย่างน้อย 1 รายการ')
                });
                return false;
            }
        }

        if (validate && body['detail'] && body['detail'].length > 0) {
            let cost = 0;
            let price = 0;
            body.detail.forEach((obj: any) => {
                cost = isEmpty(obj['cost']) ? 0 : parseFloat(obj['cost']);
                price = isEmpty(obj['price']) ? 0 : parseFloat(obj['price']);
                obj['cost'] = cost;
                obj['price'] = price;
                // Only lines actually being received must carry a valid cost.
                if (targetStatus === 2 && (Number(obj['quantity']) || 0) > 0 && cost <= 0) {
                    validate_text = "ต้นทุนสินค้าต้องมากกว่า 0";
                    validate = false;
                    return false;
                }
            });
        }

        if (!validate) {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', validate_text)
            });
            return false;
        }

        try {
            body['quantity'] = isEmpty(body['quantity']) ? 0 : body['quantity'];
            body['weight'] = isEmpty(body['weight']) ? 0 : body['weight'];
            body['cost'] = isEmpty(body['cost']) ? 0 : parseFloat(body['cost']);
            body['vendor'] = body['vendor'] < 1 ? null : body['vendor'];
            body['purchase_order'] = body['purchase_order'] < 1 ? null : body['purchase_order'];
            body['remark'] = isEmpty(body['remark']) ? null : body['remark'];

            let res = await api_good_receive(operation, body, key_id);
            if (res === 1 || (res && res.id)) {
                setOpen(false);
                const successMsg = targetStatus === 2 
                    ? 'ยืนยันรับเข้าสินค้าและปรับปรุงสต๊อกสำเร็จแล้ว' 
                    : (operation === 'UPDATE' ? 'แก้ไขฉบับร่างสำเร็จแล้ว' : 'สร้างฉบับร่างสำเร็จแล้ว');
                Modal.success({
                    ...getConfigModal('สำเร็จ', successMsg),
                    ...{ onOk() { init(); }}
                });
            } else {
                Modal.error({
                    ...getConfigModal('ผิดพลาด', 'บันทึกข้อมูลไม่สำเร็จ'),
                });
            }
        } catch (err: any) {
            console.error('Save/Confirm Good Receive error:', err);
            const errMsg = err?.response?.data?.message || err?.message || 'บันทึกข้อมูลไม่สำเร็จ';
            Modal.error({
                ...getConfigModal('ผิดพลาด', errMsg),
            });
        }
    };

    const onSave = async () => {
        await performSave(goodReceive.status === 2 ? 2 : 1);
    };

    const onDelete = async (key_id: number) => {
        Modal.confirm({
            ...getConfigModal('แจ้งเตือน', 'คุณต้องการลบรายการรับเข้าสินค้านี้หรือไม่ ?'),
            ...{
                async onOk() { 
                    try {
                        let res = await api_good_receive('DELETE', {}, key_id);
                        if (res === 1) {
                            Modal.success({
                                ...getConfigModal('สำเร็จ', 'ลบข้อมูลสำเร็จแล้ว'),
                                ...{ onOk() { init(); }}
                            });
                        } else {
                            Modal.error({
                                ...getConfigModal('ผิดพลาด', 'ลบข้อมูลไม่สำเร็จ'),
                            });
                        }
                    } catch (err: any) {
                        const errMsg = err?.response?.data?.message || err?.message || 'ลบข้อมูลไม่สำเร็จ';
                        Modal.error({
                            ...getConfigModal('ผิดพลาด', errMsg),
                        });
                    }
                }
            }
        });
    };

    const onOpenPrintVoucher = async (record: any) => {
        let fullData = record;
        if (!record.detail || record.detail.length === 0) {
            try {
                const res = await api_good_receive('GET', {}, record.id);
                if (res && res.id) {
                    fullData = res;
                }
            } catch (err) {
                console.error('Fetch GR details for print error:', err);
            }
        }
        setPrintData(fullData);
        setIsPrintModalOpen(true);
    };

    const onDataDetailChange = (payload: any) => {
        setGoodReceive(payload);
    };

    useEffect(() => {
        if (location.pathname === '/good/receive') {
            init();
        }
    }, [location.pathname]);

    return (
        <div className="pm-page-container">
            <div className="pm-header-row">
                <div className="pm-header-title-box">
                    <h2>
                        <FontAwesomeIcon icon="truck-ramp-box" style={{ color: 'var(--pm-primary, #FF7A59)' }} />
                        รับสินค้าเข้าสต๊อก (Goods Receive - GR)
                    </h2>
                    <p>บันทึกการรับสินค้าเข้าคลัง นำเข้าจากใบสั่งซื้อ (PO) และจัดการล็อตสินค้า</p>
                </div>
            </div>

            <CommonTableList 
                page='good-receive' 
                placeholder={placeholder_txt} 
                columns={columns} 
                data={[...data]} 
                filterField={filterField} 
                onAdd={() => { onShowDetail('ADD'); }} 
            />
            <CommonDetail 
                width='100%' 
                mode={mode} 
                title={title} 
                open={open} 
                onClosed={onCloseDetail} 
                onSaved={onSave} 
                recordActor={{ name: goodReceive?.create_by_name, at: (goodReceive as any)?.create_date_iso ?? goodReceive?.create_date }}
                element={ 
                    <GoodReceiveDetail 
                        goodReceive={{...goodReceive}} 
                        onChange={onDataDetailChange} 
                        onSaveDraft={() => performSave(1)}
                        onConfirmReceive={() => performSave(2)}
                    /> 
                } 
            />

            {/* Good Receive Print Voucher Modal */}
            <Modal
                title={<span><FontAwesomeIcon icon="print" style={{ marginRight: '8px', color: 'var(--primary, #FF7A59)' }} />พิมพ์ใบรับเข้าสินค้า</span>}
                open={isPrintModalOpen}
                onCancel={() => setIsPrintModalOpen(false)}
                footer={[
                    <Button key="close" className="btn-cancel" onClick={() => setIsPrintModalOpen(false)}>
                        ปิด
                    </Button>,
                    <Button
                        key="print"
                        type="primary"
                        className="btn-common"
                        onClick={() => printGoodReceiveDocument(printRef.current)}
                    >
                        <FontAwesomeIcon icon="print" /> พิมพ์เอกสาร
                    </Button>
                ]}
                width={840}
                centered
                destroyOnClose
            >
                <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '8px' }}>
                    <GoodReceivePrint ref={printRef} data={printData} />
                </div>
            </Modal>
        </div>
    );
};

export default GoodReceive;